package com.ilussobsa.views

import com.ilussobsa.*
import com.ilussobsa.Strings
import com.ilussobsa.game.*
import com.ilussobsa.sdk.*
import com.ilussobsa.utils.*
import com.lightningkite.UUID
import com.lightningkite.kiteui.*
import com.lightningkite.kiteui.models.*
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.navigation.dialogPageNavigator
import com.lightningkite.kiteui.navigation.screenNavigator
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.reactive.AppState
import com.lightningkite.kiteui.reactive.WindowInfo
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.canvas.*
import com.lightningkite.kiteui.views.direct.*
import com.lightningkite.kiteui.views.l2.icon
import com.lightningkite.lightningdb.*
import com.lightningkite.now
import com.lightningkite.reactive.context.*
import com.lightningkite.reactive.core.*
import com.lightningkite.reactive.extensions.*
import com.lightningkite.reactive.lensing.*
import com.lightningkite.readable.*
import kotlin.io.encoding.ExperimentalEncodingApi
import kotlin.random.Random
import kotlin.time.DurationUnit
import kotlinx.coroutines.*
import kotlinx.datetime.Instant

@Routable("live-auction-old/{id}")
class LiveAuctionScreenOld(val id: UUID) : Page, FullScreenAuction {

    val BASE52 = "0123456789BCDFGHJKLMNPQRSTVWXYZbcdfghjklmnpqrstvwxyz"

    val defaultAuctioneer = PersistentProperty<Auctioneer?>("defaultAuctioneer", Auctioneer.Recording1)

    data class LocalPoke(
        val message: String,
        override val at: Instant = now(),
    ) : HasTimestamp

    enum class AuctionState {
        NotOpen,
        Waiting,
        Open
    }

    @OptIn(ExperimentalEncodingApi::class)
    override fun ViewWriter.render(): ViewModifiable {
        val auction = remember { currentSession().auctionLane.get(id)() }
        val live = remember { currentSession().liveAuctionData.watch(id)() }
        val runlistMeta = remember {
            val isNextAuction = currentSession().auctionLane.query(Query(
                orderBy = sort { it.scheduledStart.ascending() },
                limit = 1
            ))().firstOrNull()?._id == id
            currentSession().vehicle.watch(
                Query(
                    orderBy = sort { it.orderingValue.ascending() },
                    limit = 1000,
                    condition = if (isNextAuction) {
                            auction.awaitNotNull().runlist or auction.awaitNotNull().submittedAwaitingAssignment
                        } else {
                            auction.awaitNotNull().runlist
                        }
                )
            )
        }
        val runlist = remember { runlistMeta()() }
        val bidsMeta = remember {
            currentSession().bid.watch(
                Query(orderBy = sort { it.price.ascending(); it.at.ascending() },
                    limit = 1000,
                    condition = condition { it.vehicle eq live.awaitNotNull().currentVehicle })
            )
        }
        val currentLotBids = remember {
            // Post process to hide equal value non-winning bids
            bidsMeta()().distinctBy { it.price }
        }
        val talkingMeta = remember {
            currentSession().sellerTalkingPoint.watch(
                Query(orderBy = sort { it.at.ascending() },
                    limit = 1000,
                    condition = condition { it.vehicle eq live.awaitNotNull().currentVehicle })
            )
        }
        val talking = remember { talkingMeta()() }
        val localPokes = Signal<List<LocalPoke>>(listOf())
        val biddingStart = remember { live()?.startedAt ?: now() }
        val saleHistory = remember { (talking() + currentLotBids()).sortedBy { it.at }.takeUnless { it.isEmpty() } ?: listOf(listOf(LocalPoke("No bids yet", biddingStart()))) }
        val nowBySecond = Signal(now())
        val playSecondaryAnimation = BasicListenable()
        var currentVideoSrc: VideoSource? = null

        var currentAnimationAction:Signal<AnimationAction> = Signal(AnimationAction.IDLE)

//        val currentLotId = remember { runlist.await().firstOrNull()?._id }
//        val laneState = remember { AuctionState.Open }
        val currentLotId = remember { live()?.currentVehicle }
        val laneState = remember {
            live()?.let {
                if (nowBySecond() > it.laneStartedAt) AuctionState.Open
                else AuctionState.Waiting
            } ?: AuctionState.NotOpen
        }

        val finalTimeout = remember {
            live()?.let {
                if ((it.increment == it.bottomIncrement || (it.dropsLeft == 0 && it.bids == 0)) && it.completion == null)
                    it.timeout
                else
                    null
            }
        }
        val currentLot =
            remember { currentLotId()?.let { currentSession().vehicle.watch(it)() } }

        return row {
            this.onRemove {
                launch {
                    currentUser()?.let {
                        currentSession().uncached.liveAuctionData.notifyLeave(id, it._id)
                    }
                }
            }
            val dontShowLoading = CoroutineScope(coroutineContext + object: StatusListener {
                override fun loading(readable: Reactive<*>) {}
            })
            dontShowLoading.reactiveSuspending {
                while (true) {
                    currentUser()?.let {
                        currentSession().uncached.liveAuctionData.notifyJoin(id, it._id)
                    }
                    delay(10_000)
                }
            }

            dontShowLoading.reactiveSuspending {
                while (true) {
                    delay(100)
                    nowBySecond.value = now()
                }
            }

            reactiveScope {
                currentLotId()
                localPokes.value = listOf()
            }

            var permitPoke = true
            val shouldPoke = remember {
                currentLotBids().let {
                    it.getOrNull(it.lastIndex - 1)?.buyer == currentUser()?._id &&
                            it.getOrNull(it.lastIndex)?.buyer != currentUser()?._id &&
                            live()?.reserveMet == true
                }
            }
            dontShowLoading.reactiveSuspending {
                if (shouldPoke()) {
                    delay(2000)
                    if (permitPoke) {
                        localPokes.value += LocalPoke(
                            defaultAuctioneer.value?.audioPack()?.pokes?.random()?.text ?: "Don't let it go!"
                        )
                    }
                    permitPoke = false
                }
            }

            // Disjoint warning
            run {
                val collectionBids = remember { currentLotBids().size }
                val liveBids = remember { live()?.bids ?: 0 }
                val mismatch = remember { collectionBids() != liveBids() }
                val isBroken = mismatch.debounce(10_000)
                dontShowLoading.reactiveScope {
                    if(isBroken()) {
                        ConsoleRoot.warn("Live: ", live())
                        ConsoleRoot.warn("Current Bids: ", currentLotBids())
                        Exception("Disjoint warning: Bid count mismatch for over 10 seconds.").report()
                    }
                }
            }

            var startup = true
            launch {
                delay(1000)
                startup = false
            }
            suspend fun playClip(includeGavel: Boolean = false, getter: (AuctioneerPack) -> EventResources) {
                // We don't play right at startup.  It sounds odd.
                if(startup) return
                val auctioneer = defaultAuctioneer.value
//                val auctioneer = currentLot.awaitOnce()?.auctioneer ?: defaultAuctioneer.awaitOnce()
                val pack = auctioneer?.audioPack() ?: return
                pack.let(getter).audio.randomOrNull()?.also { println(it) }?.load()?.play()
                if (includeGavel) Resources.auctioneerGavel.load().play()
                pack.let(getter).video.randomOrNull()?.also { println(it) }?.let {
                    currentVideoSrc = it
                    playSecondaryAnimation.invokeAll()
                }
//                (currentLot.awaitOnce()?.auctioneer ?: defaultAuctioneer.awaitOnce())?.audioPack()?.let(getter)?.load()?.play()
            }

            backgroundAudio(Resources.auctioneerCrowdBackground, 0.4f) {
                screenNavigator.currentPage() == this@LiveAuctionScreenOld && laneState() == AuctionState.Open
            }

            expanding - swapView {
                swapping(
                    current = { laneState() },
                    views = {
                        when (it) {
                            AuctionState.NotOpen -> col {
                                val auctionAlreadyHappened = remember { auction()?.scheduledStartInstant?.let { it < now() } }
                                expanding - space()
                                centered - h2 {
                                    ::content {
                                        if (auctionAlreadyHappened() == true) Strings.thankYouForYourParticipation
                                        else auction()?.name ?: ""
                                    }
                                }
                                centered - h3 {
                                    ::content {
                                        if (auctionAlreadyHappened() == true) Strings.thisAuctionAlreadyHappened
                                        else {
                                            auction()?.scheduledStartInstant
                                                ?.let(Strings::thisAuctionScheduledToOpenOn) ?: ""
                                        }
                                    }
                                }
                                centered - onlyWhen { managesAuctions() } - important - button {
                                    text(Strings.startTheAuction)
                                    onClick {
                                        currentSession().uncached.liveAuctionData.start(id)
                                        currentSession().liveAuctionData[id].invalidate()
                                    }
                                }
                                centered - card - button {
                                    ::shown { managesAuctions() }
                                    row {
                                        centered - icon(Icon.edit, Strings.edit)
                                        centered - text(Strings.configureAuction)
                                    }
                                    onClick {
                                        dialogPageNavigator.navigate(AuctionConfigureDialog(id))
                                    }
                                }
                                centered - card - link {
                                    to = { AuctionsScreen() }
                                    text(Strings.viewAuctionSchedule)
                                }
                                centered - card - link {
                                    ::shown { WindowInfo().width <= 70.rem }
                                    to = { LiveAuctionRunlistScreen(id) }
                                    row {
                                        icon {
                                            source = Icon.list
                                            description = ""
                                        }
                                        centered - text(Strings.viewRunlist)
                                    }
                                }
                                expanding - space()
                            }

                            AuctionState.Waiting -> col {
                                expanding - space()
                                centered - h2(Strings.thisAuctionWillOpenSoon)
                                centered - h3 {
//                                    launch {
//                                        playClip(getter = AuctioneerPack::welcome)
//                                    }
                                    ::content {
                                        (live.awaitNotNull().laneStartedAt - nowBySecond())
                                            .toComponents { minutes, seconds, nanoseconds ->
                                                "$minutes:${
                                                    seconds.toString().padStart(2, '0')
                                                }"
                                            }
                                    }
                                }
                                centered - card - link {
                                    ::shown { WindowInfo().width <= 70.rem }
                                    to = { LiveAuctionRunlistScreen(id) }
                                    row {
                                        icon {
                                            source = Icon.list
                                            description = ""
                                        }
                                        centered - text(Strings.viewRunlist)
                                    }
                                }
                                expanding - space()
                            }

                            AuctionState.Open -> scrolls - col {
                                rowCollapsingToColumn(60.rem) {
                                    onlyWhen { WindowInfo().width > 60.rem } - sizeConstraints(height = 18.rem) - card - gravity(
                                        Align.Start,
                                        Align.End
                                    ) - sizeConstraints(
                                        width = 20.rem,
                                        height = 18.rem
                                    ) - frame {
                                        spacing = 0.px
                                        if (Platform.current != Platform.Web) {
                                            video {
                                                showControls = false
                                                scaleType = ImageScaleType.Crop
                                                val shouldBeDancing = remember {
                                                    live()?.let { it.bids > 0 && it.completion == null } == true
                                                }
                                                reactiveSuspending {
                                                    source =
                                                        if (shouldBeDancing().also { delay(500) }) {
                                                            defaultAuctioneer.await()
                                                                ?.audioPack()?.idleAfterMoney?.randomOrNull()
                                                        } else {
                                                            defaultAuctioneer.await()?.audioPack()?.idle?.randomOrNull()
                                                        }
                                                    time set 0.0
                                                    playing set true
                                                    loop = true
                                                }
                                            }
                                            video {
                                                reactiveSuspending {
                                                    rerunOn(playSecondaryAnimation)
                                                    source = currentVideoSrc
                                                    time set 0.0
                                                    playing set true
                                                    exists = true
                                                }
                                                var wasPlaying = true
                                                reactiveScope {
                                                    val isPlaying = playing()
                                                    if (!isPlaying && wasPlaying) {
                                                        exists = false
                                                    }
                                                    wasPlaying = isPlaying
                                                }
                                                showControls = false
                                                scaleType = ImageScaleType.Crop
                                                launch {
                                                    volume set 0f
                                                    playing set true
                                                }
                                            }
                                        } else {
                                            auctioneer {
                                                reactiveSuspending {
                                                    animationAction = currentAnimationAction()
                                                    if(currentAnimationAction() != AnimationAction.COUNTING_1_ACTION && currentAnimationAction() != AnimationAction.COUNTING_2_ACTION &&
                                                        currentAnimationAction() != AnimationAction.NO_SALE_ACTION && currentAnimationAction() != AnimationAction.OFFER_ACTION && currentAnimationAction() != AnimationAction.SOLD_ACTION){
                                                        delay(100)
                                                        currentAnimationAction.set(AnimationAction.IDLE)
                                                    }
                                                }
                                            }
                                        }
                                        atTopStart - button {
                                            icon(Icon.person, Strings.auctioneer)
                                            onClick {
                                                dialogPageNavigator.navigate(object : Page {
                                                    override fun ViewWriter.render() = dismissBackground {
                                                        centered - card - col {
                                                            row {
                                                                h2(Strings.selectAuctioneer)
                                                                button {
                                                                    icon(Icon.close, Strings.close)
                                                                    onClick { navigator.dismiss() }
                                                                }
                                                            }
                                                            Auctioneer.values().forEach { auctioneer ->
                                                                radioToggleButton {
                                                                    text(auctioneer.displayName)
                                                                    checked bind defaultAuctioneer.equalTo(
                                                                        auctioneer
                                                                    )
                                                                }
                                                            }
                                                            radioToggleButton {
                                                                text(Strings.off)
                                                                checked bind defaultAuctioneer.equalTo(null)
                                                            }
                                                        }
                                                    }
                                                })
                                            }
                                        }
                                    }
                                    expanding - sizeConstraints(height = 18.rem) - frame {
                                        canvas {
                                            val delegate = AuctioneerSpeechDelegate()
                                            this.delegate = delegate
                                            var last = clockMillis()
                                            reactiveScope {
                                                rerunOn(AppState.animationFrame)
                                                val now = clockMillis()
                                                delegate.step((now - last) / 1000.0)
                                                last = now
                                            }
                                            var goingOncePlayed = false
                                            var goingTwicePlayed = false
                                            var lastAsk = 0
                                            var soldPlayed = false
                                            reactiveSuspending {
                                                rerunOn(currentLotId)
                                                delegate.status.text = ""
                                                delegate.comment.text = ""
                                                delegate.winningBidBuyer.text = "Asking Price"
                                                goingOncePlayed = false
                                                goingTwicePlayed = false
                                                soldPlayed = false
                                                lastAsk = 0
                                                val l = live.awaitOnce()
                                                if (l != null && l.completion == null) {
                                                    playClip(getter = AuctioneerPack::next)
                                                }
                                            }
                                            reactiveSuspending {
                                                val l = live.await() ?: return@reactiveSuspending
                                                val newAsk = l.asking
                                                if (newAsk < lastAsk) {
                                                    if (l.bids == 0 && l.dropsLeft == 0)
                                                        playClip(getter = AuctioneerPack::bottomPrice)
                                                }
                                                lastAsk = newAsk
                                            }

                                            var reserveMetPlayed = false
                                            var lastBids = 0
                                            reactiveSuspending {
                                                val l = live.await() ?: return@reactiveSuspending
                                                val newCount = l.bids
                                                println("newCount $newCount")
                                                if (newCount == 0) {
                                                    reserveMetPlayed = false
                                                    lastBids = 0
                                                }
                                                println("lastBids $lastBids")
                                                println("reserveMetPlayed $reserveMetPlayed")
                                                println("l.reserveMet ${l.reserveMet}")
                                                if (newCount > lastBids) {
                                                    println("newCount > lastBids")
                                                    playClip(getter = AuctioneerPack::bidPlaced)
                                                    if (!reserveMetPlayed && l.reserveMet && newCount > 0) {
                                                        reserveMetPlayed = true
                                                        playClip(getter = AuctioneerPack::reserveMet)
                                                        delegate.winningBidBuyer.text = "Reserve met!"
                                                        if(newCount == 1) {
                                                            currentAnimationAction.set(AnimationAction.LIVE_MONEY)
                                                        }
                                                        permitPoke = true
                                                        delay(100)
                                                    } else if (newCount == 1) {
                                                        currentAnimationAction.set(AnimationAction.LIVE_MONEY)
                                                        playClip(getter = AuctioneerPack::firstBidPlaced)
                                                    } else {
                                                        println("Bidding action")
                                                        currentAnimationAction.set(AnimationAction.BIDDING_ACTION)
                                                    }
                                                }
                                                lastBids = newCount
                                            }
                                            val goingStatus = remember {
                                                finalTimeout()?.let {
                                                    if (it - nowBySecond() < LiveAuctionData.lagAccounting + LiveAuctionData.goingDuration) {
                                                        2
                                                    } else if (it - nowBySecond() < LiveAuctionData.lagAccounting + LiveAuctionData.goingDuration * 2) {
                                                        1
                                                    } else {
                                                        0
                                                    }
                                                } ?: 0
                                            }
                                            reactiveSuspending {
                                                when (val gs = goingStatus().also { println("goingStatus: $it") }) {
                                                    2 -> {
                                                        delegate.status.text = Strings.goingTwice
                                                        if (!goingTwicePlayed) {
                                                            goingTwicePlayed = true
                                                            playClip(getter = AuctioneerPack::goingTwice)
                                                            currentAnimationAction.set(AnimationAction.COUNTING_1_OUT_ACTION)
                                                            delay(100)
                                                            currentAnimationAction.set(AnimationAction.COUNTING_2_ACTION)
                                                            delay(100)
                                                        }
                                                    }

                                                    1 -> {
                                                        delegate.status.text = Strings.goingOnce
                                                        if (!goingOncePlayed) {
                                                            goingOncePlayed = true
                                                            playClip(getter = AuctioneerPack::goingOnce)
                                                            currentAnimationAction.set(AnimationAction.COUNTING_1_ACTION)
                                                            delay(50)
                                                        }
                                                    }

                                                    0 -> {
                                                        goingOncePlayed = false
                                                        goingTwicePlayed = false
                                                        delegate.status.text = ""
                                                    }
                                                }
                                            }
                                            reactiveSuspending {
                                                live.await()?.completion?.let {
                                                    delegate.status.text =
                                                        if (live.await()?.reserveMet == true) "SOLD" else Strings.noSale
                                                    delegate.comment.text = ""
                                                    if (!soldPlayed) {
                                                        soldPlayed = true
                                                        playClip(
                                                            includeGavel = true,
                                                            getter = if (live.await()?.reserveMet == true) AuctioneerPack::sold else AuctioneerPack::noSale
                                                        )
                                                        if(currentAnimationAction() == AnimationAction.COUNTING_2_ACTION) {
                                                            currentAnimationAction.set(AnimationAction.COUNTING_2_OUT_ACTION)
                                                        }


                                                        println("test current animation")
                                                        currentAnimationAction.set(if (live.await()?.reserveMet == true) AnimationAction.SOLD_ACTION else AnimationAction.NO_SALE_ACTION)
                                                    }
                                                }
                                            }
                                            reactiveScope {
                                                localPokes().lastOrNull()?.let {
                                                    delegate.comment.text = it.message
                                                }
                                            }
                                            reactiveScope {
                                                val l = live()
                                                if (l?.winningBid?.buyer == currentUser()?._id && l?.completion == null) {
                                                    delegate.comment.text = ""
                                                }
                                            }
                                            reactiveScope {
                                                delegate.winningBid.text =
                                                    live()?.asking?.renderPriceInDollars() ?: ""
                                            }
                                        }
                                        atTopEnd - card - link {
                                            ::shown { WindowInfo().width <= 70.rem }
                                            to = { LiveAuctionRunlistScreen(id) }
                                            icon {
                                                source = Icon.list
                                                description = "View Runlist"
                                            }
                                        }
                                    }
                                    centered - sizeConstraints(width = 20.rem, height = 18.rem) - col {
                                        expanding - card - frame {
                                            recyclerView {
                                                children(saleHistory) {
                                                    text {
                                                        ::content {
                                                            when (val item = it()) {
                                                                is Bid -> {
                                                                    val user =
                                                                        currentSession.awaitNotNull().user[item.buyer]()
                                                                    "${user?.biddingDisplayName ?: "-"} bid ${item.price.renderPriceInDollars()}"
                                                                }

                                                                is SellerTalkingPoint -> {
                                                                    Strings.sellerSaysX(item.message)
                                                                }

                                                                is LocalPoke -> {
                                                                    item.message
                                                                }

                                                                else -> ""
                                                            }
                                                        }
                                                    }
                                                }
                                                reactiveSuspending {
                                                    val last = saleHistory.await().lastIndex
                                                    delay(100)
                                                    scrollToIndex(last, Align.End)
                                                }
                                            }
                                        }

                                        val weHaveTopBid = Signal(false)
                                        reactiveScope {
                                            weHaveTopBid.value =
                                                live()?.winningBid?.buyer.let { it != null && it == currentUser()?._id }
                                        }
                                        onlyWhen { participatesInAuctions() } - button {
                                            dynamicTheme { if (weHaveTopBid()) AffirmativeSemantic else CriticalSemantic }
                                            centered - h2 {
                                                ::content {
                                                    if (weHaveTopBid()) Strings.youHaveTheTopBid else live()?.asking?.renderPriceInDollars()
                                                        ?.let { Strings.bidX(it) } ?: "-"
                                                }
                                            }
                                            ::enabled {
                                                (live()
                                                    ?.let { nowBySecond() > (it.startedAt ?: return@let null) && it.completion == null }
                                                    ?: false)
                                            }
                                            launch {
                                                while (true) {
                                                    finalTimeout.state.getOrNull()?.let {
                                                        if (it - now() < LiveAuctionData.lagAccounting + LiveAuctionData.goingDuration) {
                                                            animateEmphasis()
                                                        }
                                                    }
                                                    delay(1100)
                                                }
                                            }
                                            onClick {
                                                if (weHaveTopBid()) return@onClick
                                                val vehicle = currentLotId.await() ?: run { return@onClick }
                                                val buyer = currentUser()?._id ?: run { return@onClick }
                                                val price = live.await()?.asking ?: run { return@onClick }
                                                currentUserSession().bid.insert(
                                                    Bid(
                                                        vehicle = vehicle,
                                                        buyer = buyer,
                                                        price = price,
                                                    )
                                                )
                                            }
                                            hoverEvents(
                                                onMouseEnter = {
                                                    currentUser()?.let {
                                                        currentSession().uncached.liveAuctionData.notifyHover(id, it._id)
                                                    }
                                                },
                                                onMouseLeave = {
                                                    currentUser()?.let {
                                                        currentSession().uncached.liveAuctionData.notifyUnhover(id, it._id)
                                                    }
                                                },
                                            )
                                        }
                                        onlyWhen { isSeller() } - col {
                                            val tf: TextField
                                            row {
                                                val messageToSend = Signal("")
                                                expanding - fieldTheme - textField {
                                                    tf = this
                                                    hint = Strings.sendSellerMessageHint
                                                    content bind messageToSend
                                                }
                                                button {
                                                    icon(Icon.send, Strings.send)
                                                    onClickAssociatedField(tf) {
                                                        currentUserSession().sellerTalkingPoint.insert(
                                                            SellerTalkingPoint(
                                                                vehicle = currentLotId.awaitNotNull(),
                                                                message = messageToSend.value
                                                            )
                                                        )
                                                        messageToSend.value = ""
                                                    }
                                                }
                                            }
                                            critical - button {
                                                centered - h2 {
                                                    ::content {
                                                        if (live.awaitNotNull().reserveMet) Strings.reserveMet else Strings.authorizeSale
                                                    }
                                                }
                                                ::enabled {
                                                    live()
                                                        ?.let { nowBySecond() > (it.startedAt ?: return@let null) && it.completion == null && !it.reserveMet }
                                                        ?: false
                                                }
                                                onClick {
                                                    val vehicle = currentLotId.await() ?: run { return@onClick }
                                                    currentUserSession().vehicle.get(vehicle).modify(modification {
                                                        it.reserve assign null
                                                    })
                                                }
                                            }
                                        }
                                        progressBar {
                                            ::shown { currentUser()?.role?.let { it > UserRole.Manager } == true }
                                            ::ratio {
                                                (live.awaitNotNull().timeout - nowBySecond()).toDouble(DurationUnit.SECONDS)
                                                    .div(20).coerceIn(0.0, 1.0).toFloat()
                                            }
                                        }
                                    }
                                }
                                swapView {
                                    swapping(
                                        current = { currentLotId() },
                                        views = { selectedLot ->
                                            if (selectedLot == null) {
                                                return@swapping col {
                                                    text(Strings.theAuctionIsntLive)
                                                    important - button {
                                                        ::shown { currentUserSessionNullable()?.me()?.role?.let { it >= UserRole.Manager } == true }
                                                        text(Strings.startNow)
                                                        onClick {
                                                            currentSession().uncached.liveAuctionData.start(id)
                                                        }
                                                    }
                                                }
                                            }

                                            return@swapping vehicleDetail(
                                                selectedLot,
                                                immutable = true,
                                                goto = {},
                                                null
                                            )
                                        }
                                    )
                                }
                            }
                        }
                    }
                )
            }

            sizeConstraints(width = 25.rem) - onlyWhen { WindowInfo().width > 70.rem } - col {
                val jumpToCurrent = BasicListenable()
                padded - frame {
                    compact - important - button {
                        centered - text(Strings.jumpToCurrent)
                        ::shown { laneState() == AuctionState.Open }
                        onClick { jumpToCurrent.invokeAll() }
                    }
                }
                expanding - padded - recyclerView {
                    children(runlist) {
                        compact - button {
                            dynamicTheme { if (currentLotId() == it()._id) SelectedSemantic else null }
                            sellingLotShortDetails(it)
                            onClick {
                                dialogPageNavigator.navigate(SellingDialogWrapper(VehicleDetailScreen(it.await()._id)))
                            }
                        }
                    }
                    reactiveScope {
                        rerunOn(jumpToCurrent)
                        val current = currentLotId.awaitOnce()
                        val index = runlist.awaitOnce().indexOfFirst { it._id == current }
                        if (index != -1) scrollToIndex(index, Align.Start)
                    }
                }
            }
        }
    }
}

class AuctioneerSpeechDelegate : GameDelegate() {
    val status = AnimatedChangingText(isTitle = true)
    val winningBid = AnimatedChangingText(isTitle = true)
    val winningBidBuyer = AnimatedChangingText()
    val comment = AnimatedChangingText()

    init {
        comment.announcementDestination.x = 500.0
        comment.announcementDestination.y = 900.0
        comment.destination.x = 500.0
        comment.destination.y = 900.0
        comment.fixedTextSize = 200.0
        comment.text = "Comment"
        comment.fullRadius = 500.0
        comment.endRadius = 400.0
        objects.add(comment)
        status.announcementDestination.x = 500.0
        status.announcementDestination.y = 750.0
        status.destination.x = 500.0
        status.destination.y = 750.0
        status.text = "Status"
        status.fullRadius = 700.0
        status.endRadius = 600.0
        objects.add(status)
        winningBid.announcementDestination.x = 500.0
        winningBid.announcementDestination.y = 400.0 - 100.0
        winningBid.destination.x = 500.0
        winningBid.destination.y = 400.0 - 100.0
        winningBid.text = "$100,000"
        winningBid.fixedTextSize = 280.0
        winningBid.fullRadius = 900.0
        winningBid.endRadius = 800.0
        objects.add(winningBid)
        winningBidBuyer.announcementDestination.x = 500.0
        winningBidBuyer.announcementDestination.y = 550.0 - 100.0
        winningBidBuyer.destination.x = 500.0
        winningBidBuyer.destination.y = 550.0 - 100.0
        winningBidBuyer.text = "Some Dealership"
        winningBidBuyer.fixedTextSize = 140.0
        winningBidBuyer.fullRadius = 600.0
        winningBidBuyer.endRadius = 500.0
        objects.add(winningBidBuyer)

        comment.text = "asdf"
        status.text = ""
        winningBid.text = ""
        winningBidBuyer.text = ""
    }

    fun comment(text: String) {
        val obj = CommentText()
        obj.current.x = (200..800).random().toDouble()
        obj.current.y = (600..900).random().toDouble()
        obj.fixedTextSize = 200.0
        obj.radius = 200.0
        obj.text = text
        objects.add(obj)
    }
}

class CrowdDelegate : GameDelegate() {
    class CrowdMember(val id: UUID) : PositionedGameObject() {
        override fun innerDraw(context: DrawingContext2D) = with(context) {
            fillPaint = Color.black
            beginPath()
            appendArc(0.0, -250.0, 200.0, Angle.zero, Angle.circle, true)
            closePath()
            fill()
            beginPath()
            appendArc(0.0, 100.0, 300.0, Angle.zero, Angle.circle, true)
            closePath()
            fill()
            fillRect(-300.0, 100.0, 600.0, 1200.0)
        }

        var jumping = 1.0
        var leaving = false
        fun leave() {
            leaving = true
        }

        var destYPos = 500.0
        override fun step(seconds: Double): Boolean {
            if (leaving) {
                current.y += seconds * 1000.0
            } else {
                if (jumping < 1.0) jumping += seconds
                if (current.y > destYPos) current.y -= seconds * 1000.0
                else if (jumping < 0.5) current.y -= seconds * 100.00
                else if (jumping < 1.0) current.y += seconds * 100.00
                else current.y = destYPos - 1.0
            }
            return current.y > 2000
        }
    }

    init {
        objects.add(object : GameObject {
            override fun draw(context: DrawingContext2D) {
//                context.fillPaint = appTheme.important().background
//                context.fillRect(worldLeft, worldHeight / 3, worldWidth, worldHeight / 3)
                context.fillPaint = Color.black
                context.fillRect(worldLeft, worldHeight * 2 / 3, worldWidth, worldHeight / 3)
            }
        })
        objects.add(CrowdMember(nullUuid).also { it.current.x = 500.0; it.current.y = 500.0; it.radius = 300.0 })
    }

    fun onBid(id: UUID) {
        objects.filterIsInstance<CrowdMember>().find { it.id == id }?.jumping = 0.0
    }

    fun crowdUpdate(participants: Set<UUID>) {
        val remaining = participants.toMutableSet()
        objects.toList().filterIsInstance<CrowdMember>().forEach {
            if (!remaining.remove(it.id)) {
                println("${it.id} is leaving")
                it.leave()
            }
        }
        for (id in remaining) {
            println("$id arriived")
            objects.add(CrowdMember(id).apply {
                radius = Random.nextDouble(200.0, 400.0)
//                current.x = 200.0
//                current.y = 500.0
                current.x = Random.nextDouble(worldLeft + worldWidth / 4, worldRight - worldWidth / 4)
                current.y = 1200.0
                destYPos = 500.0
            })
        }
    }
}